import {
  Component, EventEmitter, Output, OnInit, Input, OnChanges,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import dayjs from 'dayjs';

export interface IDateRangeOption {
  name: string;
  start: Date;
  end: Date;
}

@Component({
    selector: 'app-date-range-selector-predefined-ranges',
    templateUrl: './date-range-selector-predefined-ranges.component.html',
    styleUrls: ['./date-range-selector-predefined-ranges.component.scss'],
    standalone: false
})
export class DateRangeSelectorPredefinedRangesComponent implements OnInit, OnChanges {
  @Output() rangeSelected = new EventEmitter<IDateRangeOption>();

  @Input() maxDate?: Date;

  preDefinedRanges: IDateRangeOption[];

  selectRange(range: IDateRangeOption): void {
    this.rangeSelected.emit(range);
  }

  ngOnInit() {
    this.setPredefinedRanges();
  }

  ngOnChanges(): void {
    this.setPredefinedRanges();
  }

  getMatchingPredefinedRange(from: dayjs.Dayjs, to: dayjs.Dayjs): IDateRangeOption|undefined {
    return this.preDefinedRanges
      .find((range) => from.isSame(range.start, 'day') && to.isSame(range.end, 'day'));
  }

  private setPredefinedRanges() {
    const rangeNames = [
      _('date_range.today'),
      _('date_range.yesterday'),
      _('date_range.this_week'),
      _('date_range.last_week'),
      _('date_range.this_month'),
      _('date_range.last_month'),
      _('date_range.this_year'),
      _('date_range.last_year'),
    ];

    this.preDefinedRanges = rangeNames
      .map((name) => this.getRange(name))
      .filter((range) => !this.maxDate || (range.start <= this.maxDate && range.end <= this.maxDate));
  }

  private getRange(name: string): IDateRangeOption {
    switch (name.split('.').pop()) {
      case 'today': {
        return { name, start: new Date(), end: new Date() };
      }
      case 'yesterday': {
        const yesterday = dayjs().subtract(1, 'days').toDate();
        return { name, start: yesterday, end: yesterday };
      }
      case 'this_week': {
        const today = dayjs();
        return { name, start: today.weekday(0).toDate(), end: new Date() };
      }
      case 'last_week': {
        const lastWeek = dayjs().subtract(1, 'weeks');
        return {
          name,
          start: lastWeek.weekday(0).toDate(),
          end: lastWeek.weekday(6).toDate(),
        };
      }
      case 'this_month': {
        const today = dayjs();
        return { name, start: today.startOf('month').toDate(), end: new Date() };
      }
      case 'last_month': {
        const lastMonth = dayjs().subtract(1, 'months');
        return {
          name,
          start: lastMonth.startOf('month').toDate(),
          end: lastMonth.endOf('month').toDate(),
        };
      }
      case 'this_year': {
        const today = dayjs();
        return {
          name,
          start: today.startOf('year').toDate(),
          end: today.endOf('year').toDate(),
        };
      }
      case 'last_year': {
        const lastYear = dayjs().subtract(1, 'years');
        return {
          name,
          start: lastYear.startOf('year').toDate(),
          end: lastYear.endOf('year').toDate(),
        };
      }
      default:
        return { name, start: undefined, end: undefined };
    }
  }
}
